import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hero } from './Hero';
import { HeroMessages } from './Hero.messages';
import { CommonMessages } from '../../i18n/Common.messages';
import { DemoVideo } from './DemoVideo';
import { TrustStrip } from './TrustStrip';
import { ProofDifferentiation } from './ProofDifferentiation';
import { WhatYouGet } from './WhatYouGet';
import { HowItWorks } from './HowItWorks';
import { PricingPreview } from './PricingPreview';
import { FAQ } from './FAQ';
import { FinalCTA } from './FinalCTA';
import { Footer } from './Footer';
import { PageSeo } from '../seo/PageSeo';
import {
  organizationSchema,
  softwareApplicationSchema,
  faqSchema,
  breadcrumbSchema,
} from '../seo/schemas';
import { faqEntries } from './faqEntries';
import { trackEvent } from '../../utils/analytics';
import './homepage.css';

export const NewHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    trackEvent('page_view_home');
  }, []);

  const handleStartTranslating = (section: string) => {
    trackEvent('cta_click_start_translating', { section });
    navigate('/get-started');
  };

  const handleSeePricing = (section: string) => {
    trackEvent('cta_click_see_pricing', { section });
    navigate('/pricing');
  };

  const scrollToHowItWorks = () => {
    trackEvent('cta_click_see_how', { section: 'hero' });
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  /*
   * Built from the same list the visible FAQ renders, rather than a second
   * hardcoded copy. See faqEntries.ts: the two had drifted to different wording,
   * different question counts, and English on every locale.
   */
  const homepageFaq = faqSchema(faqEntries(t));

  return (
    <>
      <PageSeo
        path="/"
        localized
        titleKey="seo.home.title"
        descriptionKey="seo.home.description"
        ogImage="/og/og-home.png"
        keywords={['localization', 'i18n', 'AI translation', 'CLI', 'CI/CD', 'developer tools']}
        jsonLd={[
          organizationSchema(t),
          softwareApplicationSchema(t),
          homepageFaq,
          breadcrumbSchema([{ name: t(CommonMessages.BREADCRUMB_HOME), path: '/' }]),
        ]}
      />

      <div className="homepage">
        <Hero
          onStartTranslating={() => handleStartTranslating('hero')}
          onSeeHow={scrollToHowItWorks}
        />
        <DemoVideo />
        <TrustStrip />
        <ProofDifferentiation />
        <WhatYouGet />
        {/*
          A conversion affordance in the middle of the page.
          Measured: between the demo at y=1092 and "View Full Pricing" at y=5067 there
          was nothing to click for 3,975px on desktop, and 6,860px on mobile — 63% of a
          12.9-screen page. Placed after "what you get" and before "how it works",
          which splits the remaining stretch roughly evenly and reads as a pause
          between what the product does and how it is operated.
        */}
        <div className="mid-cta">
          <div className="container">
            <button className="btn btn-primary btn-large" onClick={() => handleStartTranslating('mid_page')}>
              {t(HeroMessages.BTN_START_TRANSLATING)}
            </button>
          </div>
        </div>
        <div id="how-it-works">
          <HowItWorks />
        </div>
        {/*
          FAQ before pricing, not after.
          The strongest argument on the site — why this is not a thin wrapper around
          an LLM — was at 75% of the page on desktop and 78% on mobile, below the
          prices. A sceptical developer decides whether the product is worth anything
          before deciding whether it is worth $108, so the objection is answered
          first now.
        */}
        <FAQ />
        <PricingPreview onViewPlans={() => handleSeePricing('pricing_preview')} />
        {/*
          The page previously ended at the FAQ, so the last thing a reader could act
          on was "View Full Pricing" at 72% — nothing for the remaining 1,937px. This
          component already existed and was never rendered anywhere.
        */}
        <FinalCTA
          onStartTranslating={() => handleStartTranslating('final_cta')}
          onSeePricing={() => handleSeePricing('final_cta')}
        />
        <Footer />
      </div>
    </>
  );
};
